import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  XMarkIcon,
  Bars3Icon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

/**
 * Sortable Column Item Component
 */
const SortableColumnItem = ({ column, isVisible, onToggle, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.field });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-lg border ${
        isDragging ? 'bg-indigo-50 border-indigo-300' : 'bg-white border-gray-200'
      } ${isVisible ? '' : 'opacity-60'}`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
        title="Drag to reorder"
      >
        <Bars3Icon className="h-5 w-5" />
      </div>

      {/* Checkbox */}
      <input
        type="checkbox"
        checked={isVisible}
        onChange={() => onToggle(column.field)}
        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Column Label */}
      <span className="flex-1 text-sm font-medium text-gray-700">
        {column.label}
      </span>

      {/* Position Indicator */}
      <span className="text-xs text-gray-400">
        {index + 1}
      </span>
    </div>
  );
};

/**
 * Column Customization Modal Component
 */
const ColumnCustomizationModal = ({ isOpen, onClose, columns, onSave, savedPreferences }) => {
  const [localColumns, setLocalColumns] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState(new Set());
  const [isSaving, setIsSaving] = useState(false);

  // Setup sensors for drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Initialize from saved preferences or defaults
  useEffect(() => {
    if (isOpen && columns) {
      // If we have saved preferences, use them
      if (savedPreferences && savedPreferences.columnOrder) {
        // Reorder columns based on saved order
        const orderedColumns = savedPreferences.columnOrder
          .map(field => columns.find(col => col.field === field))
          .filter(Boolean);
        
        // Add any new columns that weren't in saved preferences
        const existingFields = new Set(savedPreferences.columnOrder);
        const newColumns = columns.filter(col => !existingFields.has(col.field));
        
        setLocalColumns([...orderedColumns, ...newColumns]);
        
        // Set visible columns from saved preferences
        if (savedPreferences.visibleColumns) {
          setVisibleColumns(new Set(savedPreferences.visibleColumns));
        } else {
          // Default: all visible
          setVisibleColumns(new Set(columns.map(col => col.field)));
        }
      } else {
        // No saved preferences, use defaults
        setLocalColumns(columns);
        setVisibleColumns(new Set(columns.map(col => col.field)));
      }
    }
  }, [isOpen, columns, savedPreferences]);

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLocalColumns((items) => {
        const oldIndex = items.findIndex(item => item.field === active.id);
        const newIndex = items.findIndex(item => item.field === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Handle toggle visibility
  const handleToggleVisibility = (field) => {
    setVisibleColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(field)) {
        newSet.delete(field);
      } else {
        newSet.add(field);
      }
      return newSet;
    });
  };

  // Handle save
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const preferences = {
        columnOrder: localColumns.map(col => col.field),
        visibleColumns: Array.from(visibleColumns),
      };
      
      await onSave(preferences);
      toast.success('Column preferences saved successfully');
      onClose();
    } catch (error) {
      console.error('Error saving column preferences:', error);
      toast.error('Failed to save column preferences');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    // Reset to saved preferences
    if (savedPreferences && savedPreferences.columnOrder) {
      const orderedColumns = savedPreferences.columnOrder
        .map(field => columns.find(col => col.field === field))
        .filter(Boolean);
      const existingFields = new Set(savedPreferences.columnOrder);
      const newColumns = columns.filter(col => !existingFields.has(col.field));
      setLocalColumns([...orderedColumns, ...newColumns]);
      
      if (savedPreferences.visibleColumns) {
        setVisibleColumns(new Set(savedPreferences.visibleColumns));
      } else {
        setVisibleColumns(new Set(columns.map(col => col.field)));
      }
    } else {
      setLocalColumns(columns);
      setVisibleColumns(new Set(columns.map(col => col.field)));
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleCancel}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Customize Transaction Columns
            </h3>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <p className="text-sm text-gray-600 mb-4">
              Drag columns to reorder them. Check/uncheck columns to show or hide them.
              The topmost checked column will appear first in the table.
            </p>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={localColumns.map(col => col.field)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {localColumns.map((column, index) => (
                    <SortableColumnItem
                      key={column.field}
                      column={column}
                      isVisible={visibleColumns.has(column.field)}
                      onToggle={handleToggleVisibility}
                      index={index}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            {/* Info */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> If you have more columns than can fit on screen,
                the table will automatically show a horizontal scrollbar.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || visibleColumns.size === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4" />
                  Save Preferences
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColumnCustomizationModal;

